import React, { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { useSafeBackdropClose } from "../lib/modal";

/**
 * NotProceedingReasonDialog — proper modal for capturing an optional
 * reason when a project is moved into the Not-Proceeding lifecycle
 * stage. Replaces window.prompt (which is blocked in some embedded
 * webviews + hard to style on mobile).
 *
 * Auto-focuses the textarea, submits on Ctrl/Cmd+Enter, and cancels
 * on Escape. Empty reason is allowed (the user might not have one).
 */
export default function NotProceedingReasonDialog({ project, onConfirm, onCancel }) {
  const [reason, setReason] = useState("");
  const backdrop = useSafeBackdropClose(onCancel);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) onConfirm(reason);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [reason, onConfirm, onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      {...backdrop}
      data-testid="np-reason-dialog"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} style={{ color: "#B8721E" }} />
            <h3 className="font-display text-lg font-bold">Move to Not Proceeding</h3>
          </div>
          <button
            type="button"
            className="btn-ghost !p-1"
            onClick={onCancel}
            aria-label="Close"
            data-testid="np-reason-close"
          >
            <X size={14} />
          </button>
        </div>
        <p className="muted text-xs mb-3">
          <strong>{project?.name || "This project"}</strong> will be moved out of the live pipeline.
          Its kWp will no longer count towards installed or pipeline totals.
        </p>
        <label className="block mb-4">
          <div className="label !text-[10px] mb-1">Reason (optional)</div>
          <textarea
            className="input-swiss w-full"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Customer cancelled, DNO refused connection, financing fell through…"
            autoFocus
            data-testid="np-reason-textarea"
          />
          <div className="text-[10px] muted mt-1">
            Shown on the Kanban card and in Custom Report exports. Tip: Ctrl/Cmd+Enter to confirm.
          </div>
        </label>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={onCancel}
            data-testid="np-reason-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary text-xs"
            onClick={() => onConfirm(reason)}
            data-testid="np-reason-confirm"
          >
            Confirm move
          </button>
        </div>
      </div>
    </div>
  );
}
